import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { Hammer, Music, Star, Check, Loader2, Drama } from "lucide-react";

const inputCls = "w-full bg-[#121418] border border-[#272A34] rounded-md px-4 py-3 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";
const PRICE = 99;

export default function JoinDirectory() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: "artisan", name: "", craft: "", region: "", canton: "", image: "", website: "", email: "", bio: "", tags: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const perks = [
    "Fiche publique dédiée (photo, bio, coordonnées, liens)",
    "Mise en avant premium dans l'annuaire",
    "Visible par toute la communauté swiss-pagan",
    "Renouvellement annuel, résiliable à tout moment",
  ];

  const submit = async () => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/directory/join")}`;
      return;
    }
    if (!form.name.trim()) { toast.error("Indiquez un nom"); return; }
    setBusy(true);
    try {
      const applied = await api.post("/directory/apply", { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      const res = await api.post(`/directory/${applied.data.slug}/subscribe/checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Rejoindre l'annuaire" description="Artisans et groupes : figurez dans l'annuaire swiss-pagan.ch (abonnement annuel)." canonicalPath="/directory/join" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
        <div className="text-center mb-10">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-3">swiss-pagan.ch</div>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100 mb-4">Rejoindre l'annuaire</h1>
          <p className="text-lg text-stone-400 max-w-2xl mx-auto">Artisans et musiciens : présentez votre travail à toute la communauté. Abonnement annuel, publication après validation par notre équipe.</p>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2">
            <div className="rounded-2xl p-6 border border-amber-500/50 bg-gradient-to-b from-amber-950/20 to-[#121418] ember-glow sticky top-24">
              <Star className="w-9 h-9 text-amber-500 mb-3" />
              <div className="font-serif text-4xl text-stone-100">CHF {PRICE}<span className="text-stone-500 text-base ml-2">/ an</span></div>
              <ul className="space-y-3 mt-6">
                {perks.map((p, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-stone-300"><Check className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> {p}</li>)}
              </ul>
            </div>
          </div>

          <div className="md:col-span-3 space-y-3" data-testid="join-directory-form">
            <div className="flex gap-2">
              {[{ k: "artisan", label: "Artisan", Icon: Hammer }, { k: "band", label: "Groupe / Musicien", Icon: Music }, { k: "troupe", label: "Troupe / Animation", Icon: Drama }].map(({ k, label, Icon }) => (
                <button key={k} type="button" data-testid={`join-type-${k}`} onClick={() => setForm({ ...form, type: k })}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm border transition-all ${form.type === k ? "bg-amber-600 text-stone-950 font-semibold border-amber-400" : "bg-[#121418] text-stone-300 border-[#272A34] hover:border-amber-500/40"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
            <input data-testid="join-name" value={form.name} onChange={set("name")} placeholder="Nom de l'atelier / du groupe *" className={inputCls} />
            <input data-testid="join-craft" value={form.craft} onChange={set("craft")} placeholder="Métier / style (ex. Forgeron, Folk rituel)" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="join-region" value={form.region} onChange={set("region")} placeholder="Région (ex. Vaud (VD))" className={inputCls} />
              <input data-testid="join-canton" value={form.canton} onChange={set("canton")} placeholder="Canton (ex. VD)" className={inputCls} />
            </div>
            <input data-testid="join-email" value={form.email} onChange={set("email")} placeholder="Email de contact" className={inputCls} />
            <input data-testid="join-website" value={form.website} onChange={set("website")} placeholder="Site web / réseaux" className={inputCls} />
            <input data-testid="join-image" value={form.image} onChange={set("image")} placeholder="URL d'une photo" className={inputCls} />
            <input data-testid="join-tags" value={form.tags} onChange={set("tags")} placeholder="Mots-clés (séparés par ,)" className={inputCls} />
            <textarea data-testid="join-bio" value={form.bio} onChange={set("bio")} placeholder="Présentation / description" rows={4} className={inputCls} />
            <button data-testid="join-submit" onClick={submit} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-semibold bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 border border-amber-400/30 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {user ? `S'abonner — CHF ${PRICE}/an` : "Se connecter pour continuer"}
            </button>
            <p className="text-xs text-stone-500 text-center">Paiement sécurisé via Stripe. Votre fiche est publiée après validation par notre équipe.</p>
          </div>
        </div>
      </div>
    </>
  );
}
